import type { ArtMetadata, ArtTextStyle, Global, ArtDate } from "../schedule-presets/art-metadata";
import { positionMetadata } from "./metadata-targets";
import type { BuilderArtwork } from "../metadata-builder/artwork";
import { materializeDayLayout, type BuilderDayLayout, type DayLayoutAction } from "../metadata-builder/day-layouts";
import type { MetadataDay } from "./metadata-targets";

export type MetadataEventPayload =
  | { type: "position/set"; path: string; point: number[] }
  | { type: "metadata/replace"; metadata: ArtMetadata }
  | { type: "artwork/set"; artwork: BuilderArtwork }
  | { type: "style/set"; name: string; style: ArtTextStyle; field: keyof ArtTextStyle; globalIndices?: number[] }
  | { type: "week-dates/set"; globals: Global[]; formats: Record<string, string>; styles: Record<string, ArtTextStyle> }
  | { type: "formats/set"; formats: Record<string, string> }
  | { type: "global/style"; index: number; name: string; definition?: ArtTextStyle }
  | { type: "day-layout/set"; layout: BuilderDayLayout; styles: Record<string, ArtTextStyle>; action: DayLayoutAction; day?: MetadataDay; dates?: Partial<Record<MetadataDay, ArtDate>>; formats?: Record<string, string> }
  | { type: "day-dates/set"; dates: Partial<Record<MetadataDay, ArtDate>>; styles: Record<string, ArtTextStyle>; formats: Record<string, string> }
  | { type: "global/rotation"; index: number; rotation: number };

export type MetadataEvent = MetadataEventPayload & {
  id: string;
  at: number;
  mergeKey?: string;
};

export function applyMetadataEvent(metadata: ArtMetadata, event: MetadataEventPayload) {
  switch (event.type) {
    case "position/set": return positionMetadata(metadata, event.path, event.point);
    case "metadata/replace": return event.metadata;
    case "artwork/set": return metadata;
    case "day-layout/set": {
      const withDates = applyDayDates(metadata, event.dates ?? {}, event.formats ?? {});
      return materializeDayLayout(withDates, event.layout, event.styles);
    }
    case "day-dates/set": {
      const withDates = applyDayDates(metadata, event.dates, event.formats);
      return { ...withDates, styles: { ...withDates.styles, ...event.styles } };
    }
    case "style/set": {
      const globals = event.globalIndices
        ? metadata.globals.map((element, index) => event.globalIndices!.includes(index) ? { ...element, style: event.name } : element)
        : metadata.globals;
      return { ...metadata, globals, styles: { ...metadata.styles, [event.name]: event.style } };
    }
    case "week-dates/set": return {
      ...metadata,
      globals: [...metadata.globals.filter(element => !element.bind.startsWith("week.")), ...event.globals],
      formats: { ...metadata.formats, ...event.formats },
      styles: { ...metadata.styles, ...event.styles },
    };
    case "formats/set": return { ...metadata, formats: { ...metadata.formats, ...event.formats } };
    case "global/style": {
      if (!metadata.globals[event.index]) return metadata;
      const globals = metadata.globals.map((element, index) => index === event.index ? { ...element, style: event.name } : element);
      const styles = event.definition ? { ...metadata.styles, [event.name]: event.definition } : metadata.styles;
      return { ...metadata, globals, styles };
    }
    case "global/rotation": {
      if (!metadata.globals[event.index] || !Number.isFinite(event.rotation)) return metadata;
      const globals = metadata.globals.map((element, index) => index === event.index ? { ...element, rotation: event.rotation } : element);
      return { ...metadata, globals };
    }
  }
}

function applyDayDates(metadata: ArtMetadata, dates: Partial<Record<MetadataDay, ArtDate>>, formats: Record<string, string>) {
  const days = { ...metadata.days };
  Object.entries(dates).forEach(([key, date]) => {
    const day = key as MetadataDay;
    days[day] = { ...days[day], date };
  });
  return { ...metadata, days, formats: { ...metadata.formats, ...formats } };
}

export function projectMetadata(seed: ArtMetadata, events: MetadataEvent[], cursor: number) {
  return events.slice(0, cursor).reduce(applyMetadataEvent, seed);
}
